import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import i18n from '@/lib/i18n'

interface OverrideRow {
  key: string
  language_code: string
  value: string
}

/**
 * Fetches all translation overrides from Supabase and merges them into i18next.
 * Called once at app root — overrides win over bundled locale JSONs.
 * Uses i18n.addResourceBundle with deep-merge + overwrite flags.
 */
export function useTranslationOverrides() {
  const { data: overrides } = useQuery<OverrideRow[]>({
    queryKey: ['translation-overrides'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('translation_overrides')
        .select('key, language_code, value')
      if (error) throw error
      return data ?? []
    },
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    if (!overrides?.length) return

    // Group rows by language code
    const byLang: Record<string, Record<string, string>> = {}
    for (const { key, language_code, value } of overrides) {
      if (!byLang[language_code]) byLang[language_code] = {}
      byLang[language_code][key] = value
    }

    // Patch i18next — deep merge (true) + overwrite existing keys (true)
    for (const [lang, patch] of Object.entries(byLang)) {
      i18n.addResourceBundle(lang, 'translation', patch, true, true)
    }
  }, [overrides])
}
